import React from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import {
  createSmsTemplate,
  updateSmsTemplate,
} from "../redux/actions/smsTemplateActions";
import { cleanFormData } from "../helpers/formHelpers";
import { smsTemplateActions } from "../redux/slices/smsTemplate";

import { SmsTemplateResponseDto } from "../shared/dtos/sms/SmsTemplateResponseDto";

const smsTemplateSchema = Yup.object().shape({
  templateCode: Yup.string().required(
    "Template Code is required (e.g., FEE_REMINDER)",
  ),
  messageBody: Yup.string().required("Message Body is required"),
});

interface CreateOrUpdateSmsTemplateFormProps {
  template?: SmsTemplateResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateSmsTemplateForm: React.FC<
  CreateOrUpdateSmsTemplateFormProps
> = ({ template = null, isEditing = false, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const initialValues = {
    templateCode: template?.templateCode || "",
    messageBody: template?.messageBody || "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(smsTemplateActions.setServerStatus(null))}
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={smsTemplateSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          if (isEditing && template) {
            dispatch(updateSmsTemplate({ id: template.id, values: payload }));
          } else {
            dispatch(createSmsTemplate(payload));
          }
        }}
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={3}>
              <FormTextField
                name="templateCode"
                label="Template Code (Unique)"
                placeholder="e.g., WELCOME_MSG"
                disabled={isEditing}
              />
              <FormTextField
                name="messageBody"
                label="Message Body"
                multiline
                rows={5}
                placeholder="Hello {name}, welcome to our store..."
              />
              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Template"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateSmsTemplateForm;
